import React, { useState, useEffect } from "react";
import "../styles/home.css";
import "../styles/blogs.css";

export default function BlogThree() {
  const [sticky, setSticky] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const root = document.getElementById("root");
    if (root) {
      root.classList.add("home-root");
      root.style.flexGrow = "1";
    }

    const handleScroll = () => {
      setSticky(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="home-page">
     
      {/* Header / Navbar */}
      <header className={`navbar ${sticky ? "sticky" : ""}`}>
        <div className="nav-container">
          <div className="nav-left">
            <a href="/" className="logo">Roam</a>
            <nav>
              <a href="/#features" className="fs-14 color-low">Features</a>
              <a href="/#resources" className="fs-14 color-low">Resources</a>
              <a href="/#faqs" className="fs-14 color-low">FAQs</a>
              <a href="/#pricing" className="fs-14 color-low">Pricing</a>
            </nav>
          </div>
          <div className="nav-right">
            <a href="/login" className="btn-transparent" target="_blank" rel="noreferrer">Login</a>
            <a href="/register" className="btn-white" target="_blank" rel="noreferrer">Sign Up</a>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <section className="blog-wrapper section-features">
        <div className="blog-container">
          <div className="blog-body-text">
            
            {/* Header Block */}
            <h1 className="blog-main-title">
              Travel Healthcare Overtime Pay: What Counts and What to Check Before You Sign
            </h1>
            <p className="blog-lead-paragraph" style={{ fontStyle: 'italic', opacity: '0.80', fontWeight: '300' }}>
              Contracted hours, extra hours, overtime hours, call-back pay, and shift differentials can all produce different rates. The contract should explain each one before the first shift.
            </p>

            {/* Intro */}
            <h3 className="blog-section-title">Why the overtime line deserves more attention</h3>
            <p className="blog-paragraph">
              Travel healthcare professionals often evaluate an assignment based on the standard 36- or 40-hour schedule. In practice, overtime, extra shifts, nights, weekends, call-back work, and holiday coverage can materially change the value of the contract. A weak overtime rate can leave money on the table. An unrealistic assumption about available overtime can make an ordinary contract look exceptional.
            </p>
            <p className="blog-paragraph">
              Before accepting an offer, separate five ideas that recruiters and contracts sometimes blend together: contracted hours, extra hours, federal or state overtime hours, shift differentials, and premium rates.
            </p>

            {/* Section 1 */}
            <h3 className="blog-section-title">Contracted hours are not automatically overtime hours</h3>
            <p className="blog-paragraph">
              Suppose a contract guarantees 36 hours per week. The agency may offer an "extra hours" rate for hours worked beyond 36. Under the standard federal rule, however, covered nonexempt employees generally earn overtime after 40 hours in a workweek. Hours 37 through 40 may therefore be extra contract hours without being federal overtime hours.
            </p>
            <p className="blog-paragraph">
              The contract can still pay a premium after 36 hours, but the document should state the rate clearly. Do not assume that "extra" and "overtime" mean the same thing.
            </p>

            {/* Section 2 */}
            <h3 className="blog-section-title">The standard federal overtime rule</h3>
            <p className="blog-paragraph">
              The Fair Labor Standards Act generally requires covered nonexempt employees to receive overtime pay after 40 hours in a workweek at no less than one and one-half times the employee's regular rate. A workweek is a fixed, recurring period of 168 hours. Employers generally cannot average hours across two weeks to avoid overtime under the standard system.
            </p>

            {/* Section 3 */}
            <h3 className="blog-section-title">The healthcare 8-and-80 system</h3>
            <p className="blog-paragraph">
              Section 7(j) of the FLSA allows hospitals and residential care establishments to use a fixed 14-day work period instead of the standard 40-hour workweek when the requirements are met. A prior agreement or understanding with affected employees must exist before the work is performed.
            </p>
            <p className="blog-paragraph">
              Under this system, the employer pays overtime for hours worked over eight in a workday and over 80 in the 14-day period. An employer may use the standard system for some employees and the 8-and-80 system for others, but it cannot apply both systems to the same employee at the same time.
            </p>

            {/* Section 4 */}
            <h3 className="blog-section-title">The regular rate may be higher than the base rate</h3>
            <p className="blog-paragraph">
              Overtime is not always calculated from the printed base wage alone. The Department of Labor explains that the regular rate generally reflects total qualifying compensation divided by total hours worked. Shift differentials and many nondiscretionary bonuses may need to be included.
            </p>
            <p className="blog-paragraph">
              For example, a worker earns $30 per hour for 32 day-shift hours and receives a $4 night differential for 16 night-shift hours. Straight-time compensation is:
            </p>
            <ul className="blog-list">
              <li>32 hours x $30 = $960.</li>
              <li>16 hours x $30 = $480.</li>
              <li>16 hours x $4 differential = $64.</li>
              <li><strong>Total straight-time compensation:</strong> $1,504.</li>
              <li><strong>Regular rate:</strong> $1,504 / 48 hours = approximately $31.33 per hour.</li>
            </ul>
            <p className="blog-paragraph">
              Under the standard 40-hour method, the additional half-time premium would be approximately $15.67 per overtime hour. The resulting overtime rate is approximately $47.00 for the eight overtime hours. This simplified example illustrates the regular-rate concept; actual payroll must follow the applicable contract and law.
            </p>

            {/* Questions Section */}
            <h3 className="blog-section-title">Questions to ask about overtime and premium work</h3>
            <ol className="blog-paragraph" style={{ paddingLeft: '24px', listStyleType: 'decimal' }}>
              <li style={{ marginBottom: '12px' }}>When do extra hours begin under the contract?</li>
              <li style={{ marginBottom: '12px' }}>When do overtime hours begin under the payroll system?</li>
              <li style={{ marginBottom: '12px' }}>What is the stated overtime rate?</li>
              <li style={{ marginBottom: '12px' }}>Are night, weekend, and charge differentials included in the regular-rate calculation?</li>
              <li style={{ marginBottom: '12px' }}>How are holiday hours paid?</li>
              <li style={{ marginBottom: '12px' }}>What are the on-call and call-back rates?</li>
              <li style={{ marginBottom: '12px' }}>Is there a minimum number of paid hours for call-back work?</li>
              <li style={{ marginBottom: '12px' }}>Does the facility use a standard 40-hour system or a valid 8-and-80 system?</li>
              <li style={{ marginBottom: '12px' }}>Are overtime shifts realistically available, or is the projection merely hypothetical?</li>
              <li style={{ marginBottom: '12px' }}>Can the facility cancel an overtime shift without compensation?</li>
            </ol>

            {/* Section 5 */}
            <h3 className="blog-section-title">Track actual hours instead of relying on memory</h3>
            <p className="blog-paragraph">
              A contract estimate is useful before the assignment. A shift log becomes useful after the assignment starts. Record clock-in and clock-out times, unpaid meal periods, shift type, differentials, on-call time, call-back time, and overtime. Compare the record with each pay statement while corrections are still easy to request.
            </p>
            <p className="blog-paragraph">
              Roam Pays includes a Shift tab designed to calculate hourly and overtime earnings as work is performed. Keeping the contract estimate and the actual shift history in the same system can make discrepancies easier to notice.
            </p>

            {/* Section 6 */}
            <h3 className="blog-section-title">Do not choose a contract based on overtime that may never happen</h3>
            <p className="blog-paragraph">
              A recruiter may explain how much the assignment could pay with one or two additional shifts every week. Treat that as a scenario, not guaranteed income, unless the hours are guaranteed in writing. Compare the base contract first. Then model overtime as an optional upside case.
            </p>
            <p className="blog-paragraph">
              A useful comparison includes at least three scenarios: scheduled hours only, a realistic overtime level, and a high-overtime level. If the assignment only makes financial sense in the most optimistic scenario, the risk belongs in the decision.
            </p>
            
            {/* Call To Action */}
            <hr className="blog-divider" />
            <p className="blog-paragraph" style={{ color: '#ffffff' }}>
              Use Roam Pays to estimate regular and overtime earnings, compare multiple scenarios, and track actual shifts throughout your assignment.
            </p>

            {/* Footer Notes */}
            <hr className="blog-divider" />
            <p className="blog-disclaimer">
              Disclaimer: This article provides general educational information and is not legal, payroll, tax, or employment advice. Federal and state rules, exemptions, collective bargaining agreements, and contract terms may change the result.
            </p>
            
            <h5 className="blog-sources-title">Sources</h5>
            <ul className="blog-sources-list">
              <li>U.S. Department of Labor Overtime Pay</li>
              <li>U.S. Department of Labor Fact Sheet #54, Health Care Overtime</li>
              <li>U.S. Department of Labor Fact Sheet #53, Health Care Hours Worked</li>
              <li>Roam Pays App Store listing</li>
            </ul>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" id="contact">
        <div className="container footer-grid">
          <div className="footer-left flex-grow-1">
            <div className="footer-brand">
              <a href="/" className="logo">Roam</a>
              <p className="color-secondary fs-16">
                Per Diem Calculator for <br /> Travel Healthcare Professionals
              </p>
              <div className="social-icons">
                <a href="#" title="Dribble">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M17.3569 21.2708C19.8824 19.6656 21.6391 16.9587 21.9505 13.8265C19.3663 13.8357 17.4497 13.9085 15.557 14.4783C16.3697 16.5938 16.9709 18.8301 17.3569 21.2708ZM13.6949 15.2082C11.6566 16.1964 9.37679 17.923 6.24199 21.0021C7.86997 22.1506 9.8562 22.8252 12 22.8252C13.2216 22.8252 14.3921 22.6061 15.4743 22.2052C15.1192 19.675 14.5276 17.3787 13.6949 15.2082ZM14.7783 12.626C17.0449 11.9078 19.276 11.8336 21.9507 11.826C21.7731 10.0355 21.1232 8.38385 20.1273 6.99746C18.1435 8.53194 16.1205 9.77561 13.8293 10.7132C14.1665 11.343 14.4829 11.9797 14.7783 12.626ZM11.9271 11.4C12.2809 12.0474 12.6105 12.7008 12.9162 13.3635C10.5333 14.4996 8.01868 16.4408 4.72628 19.6877C3.0361 17.8968 2 15.482 2 12.8252L2 12.8168C5.92271 12.7502 9.12157 12.2843 11.9271 11.4ZM12.8277 8.96034C14.9834 8.10829 16.8941 6.95835 18.8014 5.49428C17.0169 3.83795 14.6267 2.8252 12 2.8252C10.8851 2.8252 9.81273 3.00766 8.81125 3.34436C10.3405 5.2239 11.6804 7.07118 12.8277 8.96034ZM6.92979 4.20401C8.44524 6.03834 9.76388 7.82028 10.889 9.62555C8.46507 10.3377 5.66455 10.7384 2.20261 10.8126C2.77743 7.99949 4.53338 5.61641 6.92979 4.20401Z" fill="#A2ABB8" />
                  </svg>
                </a>
                <a href="#" title="X">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path d="M9.08612 15.5389C8.89301 15.2915 8.52236 15.2811 8.31569 15.5173L4.24744 20.1667C3.88375 20.5823 3.92587 21.2141 4.34151 21.5778C4.75715 21.9415 5.38891 21.8993 5.75259 21.4837L9.69475 16.9784C9.85271 16.7979 9.86021 16.5306 9.71261 16.3415L9.08612 15.5389Z" fill="#A2ABB8" />
                    <path d="M15.6897 10.127C15.483 10.3632 15.1124 10.3528 14.9193 10.1054L14.2928 9.30279C14.1452 9.11369 14.1527 8.84642 14.3107 8.66588L18.2474 4.1667C18.6111 3.75107 19.2429 3.70895 19.6585 4.07263C20.0742 4.43631 20.1163 5.06808 19.7526 5.48371L15.6897 10.127Z" fill="#A2ABB8" />
                    <path d="M20.8999 21.0175L7.78057 4.20989C7.59106 3.96711 7.30026 3.8252 6.99228 3.8252H3.50483C3.08836 3.8252 2.85443 4.30455 3.11069 4.63285L16.23 21.4405C16.4195 21.6833 16.7103 21.8252 17.0183 21.8252H20.5057C20.9222 21.8252 21.1561 21.3458 20.8999 21.0175Z" fill="#A2ABB8" />
                  </svg>
                </a>
                <a href="#" title="Youtube">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M5.58971 5.03461C3.72849 5.16074 2.29215 6.64213 2.1711 8.50369C2.08072 9.89371 2 11.5048 2 12.8252C2 14.1456 2.08072 15.7567 2.1711 17.1467C2.29215 19.0083 3.72849 20.4896 5.58971 20.6158C7.21433 20.7259 9.40027 20.8252 12 20.8252C14.5997 20.8252 16.7857 20.7259 18.4103 20.6158C20.2715 20.4896 21.7078 19.0083 21.8289 17.1467C21.9193 15.7567 22 14.1456 22 12.8252C22 11.5048 21.9193 9.89371 21.8289 8.50369C21.7078 6.64213 20.2715 5.16074 18.4103 5.03461ZM11.5521 9.99299C10.8873 9.55284 10 10.0295 10 10.8268V14.8239C10 15.6211 10.8873 16.0978 11.552 15.6577L14.5454 13.6759C15.1546 13.2726 15.1547 12.3783 14.5455 11.975L11.5521 9.99299Z" fill="#A2ABB8" />
                  </svg>
                </a>
                <a href="#" title="Messenger">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 22.8252C17.5228 22.8252 22 18.348 22 12.8252C22 7.30235 17.5228 2.8252 12 2.8252C6.47715 2.8252 2 7.30235 2 12.8252C2 15.0764 2.74389 17.1539 3.99927 18.8252L3.99977 21.596C3.99989 22.2301 4.58261 22.704 5.20344 22.5749L8 21.9931C9.22492 22.5283 10.5778 22.8252 12 22.8252ZM6.70509 14.5322L9.45568 10.6028C9.76078 10.1669 10.3537 10.0463 10.8049 10.3283L13.4525 11.983C13.7858 12.1913 14.2102 12.1851 14.5372 11.9671L16.7092 10.519C16.8783 10.4063 17.1034 10.4286 17.2471 10.5723C17.3934 10.7186 17.4135 10.9487 17.2949 11.1182L14.5443 15.0476C14.2392 15.4835 13.6463 15.6041 13.1951 15.3221L10.5475 13.6674C10.2142 13.4591 9.78981 13.4653 9.4628 13.6833L7.29077 15.1313C7.1217 15.2441 6.89657 15.2218 6.75289 15.0781C6.60663 14.9318 6.58647 14.7017 6.70509 14.5322Z" fill="#A2ABB8" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="footer-contact color-secondary">
              <div className="fs-18">Roam Pays App LLC</div>
              <a href="mailto:eric@roampays.com" className="color-secondary fs-16 line-height-1" target="_blank" rel="noreferrer">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px', verticalAlign: 'middle'}}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                eric@roampays.com
              </a>
              <a href="tel:+17014047593" className="color-secondary fs-16 line-height-1" target="_blank" rel="noreferrer">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px', verticalAlign: 'middle'}}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                (701) 404-7593
              </a>
              <div className="fs-16 line-height-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px', verticalAlign: 'middle'}}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                3003 32ND Ave S Ste 240, Fargo, ND 58103-6118
              </div>
            </div>
          </div>

          <div className="footer-links">
            <div>
              <h6>Links</h6>
              <a href="/#features" className="fs-14 color-secondary">Features</a>
              <a href="/#resources" className="fs-14 color-secondary">Resources</a>
            </div>
            <div>
              <h6>Company</h6>
              <a href="/#about" className="fs-14 color-secondary">About</a>
              <a href="/#contact" className="fs-14 color-secondary">Contact</a>
            </div>
            <div>
              <h6>Support</h6>
              <a href="/#help" className="fs-14 color-secondary">Help Center</a>
              <a href="/terms" className="fs-14 color-secondary">Terms</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom fs-14 fs-helvetica-neue">
          <div className="footer-bottom-left">
            <a href="/terms" className="color-secondary">Terms & Conditions</a>
            <a href="/privacy" className="color-secondary">Privacy Policy</a>
          </div>
          <div className="footer-bottom-right color-secondary">
            Copyright © {new Date().getFullYear()} | Roam. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}